import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatListOption, MatSelectionList } from '@angular/material/list';
import { MatSlider, MatSliderRangeThumb } from '@angular/material/slider';
import { FormControlsOf } from '@examdojo/angular/forms';
import { connectState } from '@examdojo/angular/util';
import { FilterButtonComponent, FilterSectionDirective, MultipleFiltersButtonComponent } from '@examdojo/core/filters';
import { IfSmallDirective } from '@examdojo/layout';
import { StemDifficultyCategory } from '@examdojo/models/question';
import { DerivativeQuestionItemsViewStatus } from '@examdojo/question';
import { TagSelectionListComponent } from '@examdojo/tags';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { combineLatest, map, merge, Observable, tap } from 'rxjs';
import { QuestionBankQuery } from '../../app/question-bank.query';
import { QuestionBankService } from '../../app/question-bank.service';
import {
  createDifficultyLabelsMap$,
  createDifficultyOptions$,
  createSelectedDifficultyLabel$,
  createSelectedMaxMarksLabel$,
  createSelectedTagLabel$,
  createSelectedTypeLabel$,
  createTopicsFilterActiveCount$,
  createTypeLabelsMap$,
  createTypeOptions$,
} from '../question-bank-filter-labels.util';
import { QuestionBankSortComponent } from '../question-bank-sort/question-bank-sort.component';
import { QuestionBankTopicsExplorerComponent } from '../question-bank-topics-explorer/question-bank-topics-explorer.component';

interface SimpleFiltersFormValue {
  difficulties: StemDifficultyCategory[];
  types: DerivativeQuestionItemsViewStatus[];
  minPoints: number;
  maxPoints: number;
  tags: string[];
}

const INIT_FORM_STATE: SimpleFiltersFormValue = {
  difficulties: [],
  types: [],
  minPoints: 0,
  maxPoints: 0,
  tags: [],
};

@Component({
  selector: 'dojo-question-bank-simple-filters',
  templateUrl: './question-bank-simple-filters.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatSelectionList,
    MatListOption,
    ReactiveFormsModule,
    NgTemplateOutlet,
    TranslocoPipe,
    MatSlider,
    MatSliderRangeThumb,
    FilterButtonComponent,
    TagSelectionListComponent,
    IfSmallDirective,
    QuestionBankSortComponent,
    MultipleFiltersButtonComponent,
    FilterSectionDirective,
    QuestionBankTopicsExplorerComponent,
  ],
})
export class QuestionBankSimpleFiltersComponent {
  constructor(
    private readonly questionBankService: QuestionBankService,
    private readonly questionBankQuery: QuestionBankQuery,
    private readonly translocoService: TranslocoService,
  ) {
    merge(this.syncFiltersFormFromStore(), this.persistFiltersFormChanges()).pipe(takeUntilDestroyed()).subscribe();
  }

  readonly form: FormGroup<FormControlsOf<SimpleFiltersFormValue>> = new FormGroup({
    difficulties: new FormControl<StemDifficultyCategory[]>(INIT_FORM_STATE.difficulties, { nonNullable: true }),
    types: new FormControl<DerivativeQuestionItemsViewStatus[]>(INIT_FORM_STATE.types, { nonNullable: true }),
    minPoints: new FormControl(INIT_FORM_STATE.minPoints, { nonNullable: true }),
    maxPoints: new FormControl(INIT_FORM_STATE.maxPoints, { nonNullable: true }),
    tags: new FormControl<string[]>(INIT_FORM_STATE.tags, { nonNullable: true }),
  });

  readonly difficultyOptions$ = createDifficultyOptions$();
  readonly difficultyLabelsMap$ = createDifficultyLabelsMap$(this.difficultyOptions$);
  private readonly topicsFilterActiveCount$ = createTopicsFilterActiveCount$(this.questionBankQuery);
  readonly typeOptions$ = createTypeOptions$();
  readonly typeLabelsMap$ = createTypeLabelsMap$(this.typeOptions$);
  readonly selectedDifficultyLabel$ = createSelectedDifficultyLabel$(this.questionBankQuery, this.difficultyLabelsMap$);
  readonly selectedTypeLabel$ = createSelectedTypeLabel$(this.questionBankQuery, this.typeLabelsMap$);
  readonly selectedTagLabel$ = createSelectedTagLabel$(this.questionBankQuery);
  readonly selectedMaxMarksLabel$ = createSelectedMaxMarksLabel$(this.questionBankQuery, this.translocoService);

  readonly filterCounts$ = combineLatest([
    this.questionBankQuery.filtersFormValue$,
    this.questionBankQuery.selectProp('marksRange'),
  ]).pipe(
    map(([filters, marksRange]) => ({
      type: filters.types.length,
      difficulty: filters.difficulties.length,
      maxMarks: filters.maxPoints < marksRange.max || filters.minPoints > marksRange.min ? 1 : 0,
      tags: filters.tags.length,
    })),
  );

  readonly hasActiveFilters$ = combineLatest([this.filterCounts$, this.topicsFilterActiveCount$]).pipe(
    map(
      ([counts, topicsCount]) =>
        counts.type > 0 || counts.difficulty > 0 || counts.maxMarks > 0 || counts.tags > 0 || topicsCount > 0,
    ),
  );

  readonly state = connectState({
    difficultyOptions: this.difficultyOptions$,
    typeOptions: this.typeOptions$,
    selectedDifficultyLabel: this.selectedDifficultyLabel$,
    selectedTypeLabel: this.selectedTypeLabel$,
    selectedMaxMarksLabel: this.selectedMaxMarksLabel$,
    selectedTagLabel: this.selectedTagLabel$,
    hasActiveFilters: this.hasActiveFilters$,
    availableFilters: this.questionBankQuery.availableFilters$,
    filterCounts: this.filterCounts$,
    marksRange: this.questionBankQuery.selectProp('marksRange'),
    topicsCount: this.topicsFilterActiveCount$,
  });

  resetFilters(keys: Array<keyof SimpleFiltersFormValue>) {
    const marksRange = this.state.marksRange;
    const defaults: SimpleFiltersFormValue = {
      ...INIT_FORM_STATE,
      minPoints: marksRange.min,
      maxPoints: marksRange.max,
    };
    const resetValues = Object.fromEntries(keys.map((key) => [key, defaults[key]]));
    this.form.patchValue(resetValues);
  }

  clearAllFilters(): void {
    const marksRange = this.state.marksRange;
    if (!marksRange) {
      return;
    }

    this.form.reset({
      minPoints: marksRange.min,
      maxPoints: marksRange.max,
    });
    this.questionBankService.changeFilters({ topicIds: [] });
  }

  clearTopics(): void {
    this.questionBankService.changeFilters({ topicIds: [] });
  }

  private syncFiltersFormFromStore(): Observable<unknown> {
    return this.questionBankQuery.filtersFormValue$.pipe(
      tap((filters) => {
        this.form.reset(
          {
            difficulties: filters.difficulties,
            types: filters.types,
            minPoints: filters.minPoints,
            maxPoints: filters.maxPoints,
            tags: filters.tags,
          },
          { emitEvent: false },
        );
      }),
    );
  }

  private persistFiltersFormChanges(): Observable<unknown> {
    return this.form.valueChanges.pipe(
      tap((filters) => {
        this.questionBankService.changeFilters(filters);
      }),
    );
  }
}
